import { FC } from 'react';
import { Tooltip } from 'react-tooltip';
import { Button } from '@/components/ui/button';
import { Group } from '@/types/group';

type SelectedGroupsActionBarProps = {
  selectedGroups: Group[];
  hasUnitsWithDevices?: boolean;
  sendAnnouncementVisible?: boolean;
  sendResponseNotificationVisible?: boolean;
  openSendAnnouncement?: () => void;
  openSendResponseNotification?: () => void;
};

/**
 * Used on the groups page to allow actions to be performed on multiple selected
 * rows.
 */
export const SelectedGroupsActionBar: FC<SelectedGroupsActionBarProps> = ({
  selectedGroups,
  hasUnitsWithDevices = true,
  sendAnnouncementVisible = true,
  sendResponseNotificationVisible = true,
  openSendAnnouncement,
  openSendResponseNotification,
}) => {
  if (selectedGroups.length === 0) {
    return null;
  }

  const disabledTooltip =
    'No devices added to units in the selected groups. Please add a device to send';

  return (
    <div className="mb-4 mt-4 flex flex-row items-center justify-between rounded-md bg-gray-800 p-4 text-white">
      <div>
        <span className="text-sm font-medium">
          {selectedGroups.length} group{selectedGroups.length === 1 ? '' : 's'}{' '}
          selected
        </span>
      </div>
      <div className="flex flex-row gap-2">
        {sendAnnouncementVisible && selectedGroups.length > 0 && (
          <>
            <Tooltip anchorSelect="#groupAnnouncementTooltip">
              {hasUnitsWithDevices
                ? 'Send an announcement to all units in the selected groups'
                : `${disabledTooltip} announcements.`}
            </Tooltip>
            <a id="groupAnnouncementTooltip">
              <Button
                className="ml-auto"
                disabled={!hasUnitsWithDevices}
                onClick={openSendAnnouncement}
                variant="secondary"
              >
                Send announcement
              </Button>
            </a>
          </>
        )}
        {sendResponseNotificationVisible && selectedGroups.length > 0 && (
          <>
            <Tooltip anchorSelect="#groupResponseNotificationTooltip">
              {hasUnitsWithDevices
                ? 'Send a response notification to all units in the selected groups'
                : `${disabledTooltip} notifications.`}
            </Tooltip>
            <a id="groupResponseNotificationTooltip">
              <Button
                className="ml-auto"
                disabled={!hasUnitsWithDevices}
                onClick={openSendResponseNotification}
                variant="secondary"
              >
                Send response notification
              </Button>
            </a>
          </>
        )}
      </div>
    </div>
  );
};
